/*
 * INPUT COMPONENT
 *
 * RENDERS A TEXTAREA WITH AUTO-RESIZE AND A SEND BUTTON.
 * INVOKES onSend CALLBACK WITH THE CURRENT TEXT WHEN THE USER PRESSES ENTER OR CLICKS SEND.
 */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styles from './Input.module.scss';

/**
 * @param {Object} props
 * @param {(message: string) => void} props.onSend – Callback invoked with the text when sending.
 */
const Input = ({ onSend }) => {
  const [textValue, setTextValue] = useState('');
  const textareaRef = useRef(null);

  /**
   * Handles textarea changes: updates state and auto-resizes the textarea height.
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e
   */
  const handleChange = (e) => {
    setTextValue(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  /**
   * Invoked when the user clicks send or presses Enter (without Shift).
   * Trims input, calls onSend, and resets the textarea.
   */
  const handleSend = () => {
    const trimmed = textValue.trim();
    if (trimmed === '') return;
    onSend(trimmed);
    setTextValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  /**
   * Intercepts Enter key to send the message instead of inserting a newline.
   * @param {React.KeyboardEvent<HTMLTextAreaElement>} e
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={styles.inputContainer}>
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        placeholder="Skriv här..."
        value={textValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        rows={1}
      />
      <button className={styles.sendButton} onClick={handleSend} aria-label="Send message">
        <img 
          src="/assets/input-arrow.svg" 
          alt="Send" 
          className={styles.arrowIcon}
        />
      </button>
    </div>
  );
};

Input.propTypes = {
  onSend: PropTypes.func.isRequired,
};

export default Input;
